import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
    Plus, X, Upload, Loader2, CheckCircle, Link,
    ExternalLink, Image as ImageIcon,
} from 'lucide-react';
import type { CreateFormData } from '../types';
import { CATEGORY_CONFIG, EMPTY_CREATE_FORM, inputClass, labelClass } from '../constants';
import { useCreateMaterial, useExtractProduct } from '../hooks/useStoreMaterials';
import { ModalitySelector } from './ModalitySelector';
import { apiClient } from '@/services/api-client';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';

const DRAFT_KEY = 'aipeces_store_create_draft';

interface StoreCreateFormProps {
    onCreated: () => void;
    onCancel: () => void;
}

export function StoreCreateForm({ onCreated, onCancel }: StoreCreateFormProps) {
    // ── Restore draft from localStorage ────────────────────────
    const [form, setForm] = useState<CreateFormData>(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return { ...EMPTY_CREATE_FORM, ...parsed };
            }
        } catch { /* ignore */ }
        return EMPTY_CREATE_FORM;
    });
    const [localImages, setLocalImages] = useState<string[]>([]);
    const [localFiles, setLocalFiles] = useState<File[]>([]);
    const [isExtracting, setIsExtracting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [extractError, setExtractError] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [draftSaved, setDraftSaved] = useState(false);
    const [infoAlert, setInfoAlert] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const createMutation = useCreateMaterial();
    const extractMutation = useExtractProduct();

    const allImages = [...form.images, ...localImages];

    // ── Auto-save draft ────────────────────────────────────────
    useEffect(() => {
        const hasContent = form.title || form.description || form.affiliateLink || form.category;
        if (hasContent) {
            const timer = setTimeout(() => {
                localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
                setDraftSaved(true);
                setTimeout(() => setDraftSaved(false), 2000);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [form]);

    const clearDraft = () => {
        localStorage.removeItem(DRAFT_KEY);
    };

    const set = (field: keyof CreateFormData, value: string) =>
        setForm(prev => ({ ...prev, [field]: value }));

    // ── Extract product data from URL ────────────────────────
    const handleExtract = async () => {
        const url = form.affiliateLink.trim();
        if (!url) return;

        const isAliExpress = url.includes('aliexpress.com') || url.includes('s.click.aliexpress.com') || url.includes('a.aliexpress.com');
        const isDecathlon = url.includes('decathlon.es') || url.includes('decathlon.com');
        if (!isAliExpress && !isDecathlon) {
            setExtractError('El enlace debe ser de AliExpress o Decathlon');
            return;
        }

        setIsExtracting(true);
        setExtractError(null);
        try {
            const data = await extractMutation.mutateAsync(url);

            if (data.error && !data.title && !data.images?.length) {
                setExtractError(data.error);
                setIsExtracting(false);
                return;
            }

            setForm(prev => ({
                ...prev,
                title: prev.title || data.title || '',
                description: prev.description || data.description || '',
                images: data.images?.filter(img => img && typeof img === 'string' && img.trim().length > 0) || prev.images,
                price: prev.price || (data.price ? String(data.price) : ''),
                currency: data.currency || prev.currency,
                source: isAliExpress ? 'aliexpress' : 'decathlon',
                externalRating: prev.externalRating || (data.rating ? String(data.rating) : ''),
                externalReviewsCount: prev.externalReviewsCount || (data.reviews_count ? String(data.reviews_count) : ''),
            }));

            if (data.error) {
                setExtractError(`Extracción parcial: ${data.error}`);
            }
        } catch (err: any) {
            setExtractError(err.message || 'No se pudieron extraer los datos. Puedes completarlos manualmente.');
        } finally {
            setIsExtracting(false);
        }
    };

    // ── Handle file selection / drag-drop ─────────────────────
    const processFiles = (files: FileList | File[]) => {
        const validFiles: File[] = [];
        const previews: string[] = [];

        for (const file of Array.from(files)) {
            if (!file.type.startsWith('image/')) continue;
            if (file.size > 10 * 1024 * 1024) continue;
            validFiles.push(file);
        }

        validFiles.forEach(file => {
            const url = URL.createObjectURL(file);
            previews.push(url);
        });

        setLocalFiles(prev => [...prev, ...validFiles]);
        setLocalImages(prev => [...prev, ...previews]);
    };

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files.length > 0) {
            processFiles(e.dataTransfer.files);
        }
    }, []);

    const removeImage = (idx: number) => {
        if (idx < form.images.length) {
            setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
        } else {
            const localIdx = idx - form.images.length;
            setLocalImages(prev => prev.filter((_, i) => i !== localIdx));
            setLocalFiles(prev => prev.filter((_, i) => i !== localIdx));
        }
    };

    // ── Submit ────────────────────────────────────────────────
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!form.title.trim()) {
            setInfoAlert('El título es obligatorio');
            return;
        }
        if (!form.category) {
            setInfoAlert('Selecciona una categoría');
            return;
        }
        if (!form.waterType) {
            setInfoAlert('Selecciona el tipo de agua');
            return;
        }

        setIsSaving(true);
        try {
            // Upload local files
            const uploadedUrls: string[] = [];
            for (const file of localFiles) {
                const ts = Date.now();
                const ext = file.name.split('.').pop() || 'jpg';
                const path = `materials/new/${ts}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
                const result = await apiClient.uploadFile('material-images', path, file);
                if (result.data?.url) {
                    uploadedUrls.push(result.data.url);
                }
            }

            const finalImages = [...form.images, ...uploadedUrls];

            await createMutation.mutateAsync({
                p_title: form.title,
                p_description: form.description || null,
                p_images: JSON.stringify(finalImages),
                p_price: form.price ? parseFloat(form.price) : null,
                p_original_price: form.originalPrice ? parseFloat(form.originalPrice) : null,
                p_currency: form.currency,
                p_affiliate_link: form.affiliateLink || '',
                p_source: form.source,
                p_category: form.category,
                p_water_type: form.waterType,
                p_status: 'approved',
                p_external_rating: form.externalRating ? parseFloat(form.externalRating) : null,
                p_external_reviews_count: form.externalReviewsCount ? parseInt(form.externalReviewsCount) : null,
                p_recommendation_reason: form.recommendationReason || null,
                p_modalities: JSON.stringify(form.modalities),
            });

            clearDraft();
            onCreated();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            setInfoAlert(`Error al crear: ${msg}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card p-6 space-y-6">
            <AdminConfirmDialog
                open={!!infoAlert}
                intent="alert"
                title="Formulario"
                description={infoAlert}
                onCancel={() => setInfoAlert(null)}
                onConfirm={() => setInfoAlert(null)}
            />
            <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                    <Plus size={16} className="text-primary" /> Nuevo Material
                    {draftSaved && (
                        <span className="text-[10px] font-normal text-muted-foreground/60 ml-2 animate-in fade-in duration-300">
                            ✓ Borrador guardado
                        </span>
                    )}
                </h3>
                <button
                    type="button"
                    onClick={() => { clearDraft(); onCancel(); }}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                >
                    <X size={16} />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* ── Section 1: URL Extraction ─────────────────────── */}
                <div className="rounded-lg border border-border bg-muted/10 p-4 space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Link size={12} /> 1. Extracción Automática (opcional)
                    </p>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            placeholder="https://s.click.aliexpress.com/... o https://www.decathlon.es/..."
                            value={form.affiliateLink}
                            onChange={e => set('affiliateLink', e.target.value)}
                            className={cn(inputClass, 'flex-1')}
                        />
                        <button
                            type="button"
                            onClick={handleExtract}
                            disabled={isExtracting || !form.affiliateLink.trim()}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                            {isExtracting ? (
                                <><Loader2 size={14} className="animate-spin" /> Extrayendo...</>
                            ) : (
                                <><ExternalLink size={14} /> Extraer Datos</>
                            )}
                        </button>
                    </div>
                    {extractError && (
                        <p className="text-xs text-amber-400 bg-amber-500/10 rounded-md px-3 py-2">
                            ⚠️ {extractError}
                        </p>
                    )}
                    <p className="text-[10px] text-muted-foreground/60">
                        Pega un enlace de AliExpress o Decathlon para rellenar automáticamente título, imágenes, precio y valoración.
                    </p>
                </div>

                {/* ── Section 2: Product Info ───────────────────────── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className={labelClass}>Título *</label>
                        <input type="text" value={form.title} onChange={e => set('title', e.target.value)} className={inputClass} placeholder="Nombre del producto" />
                    </div>

                    <div className="md:col-span-2">
                        <label className={labelClass}>Descripción</label>
                        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} className={cn(inputClass, 'resize-y')} placeholder="Descripción del producto..." />
                    </div>

                    <div>
                        <label className={labelClass}>Categoría *</label>
                        <select value={form.category} onChange={e => set('category', e.target.value)} className={inputClass}>
                            <option value="">Seleccionar...</option>
                            {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className={labelClass}>Tipo de Agua *</label>
                        <select value={form.waterType} onChange={e => set('waterType', e.target.value)} className={inputClass}>
                            <option value="">Seleccionar...</option>
                            <option value="dulce">Agua Dulce</option>
                            <option value="salada">Agua Salada</option>
                            <option value="ambas">Ambas</option>
                        </select>
                    </div>

                    <div>
                        <label className={labelClass}>Precio (€)</label>
                        <input type="number" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} className={inputClass} placeholder="0.00" />
                    </div>

                    <div>
                        <label className={labelClass}>Precio Original (€)</label>
                        <input type="number" step="0.01" value={form.originalPrice} onChange={e => set('originalPrice', e.target.value)} className={inputClass} placeholder="0.00" />
                    </div>

                    <div>
                        <label className={labelClass}>Fuente</label>
                        <select value={form.source} onChange={e => set('source', e.target.value)} className={inputClass}>
                            <option value="aliexpress">AliExpress</option>
                            <option value="decathlon">Decathlon</option>
                            <option value="user">Usuario</option>
                        </select>
                    </div>

                    <div>
                        <label className={labelClass}>Moneda</label>
                        <select value={form.currency} onChange={e => set('currency', e.target.value)} className={inputClass}>
                            <option value="EUR">EUR (€)</option>
                            <option value="USD">USD ($)</option>
                        </select>
                    </div>

                    <div>
                        <label className={labelClass}>Valoración Externa</label>
                        <input type="number" step="0.1" min="0" max="5" value={form.externalRating} onChange={e => set('externalRating', e.target.value)} className={inputClass} placeholder="4.5" />
                    </div>

                    <div>
                        <label className={labelClass}>Nº Valoraciones</label>
                        <input type="number" min="0" value={form.externalReviewsCount} onChange={e => set('externalReviewsCount', e.target.value)} className={inputClass} placeholder="120" />
                    </div>

                    {/* Modalities Selector */}
                    <div className="md:col-span-2">
                        <label className={labelClass}>Modalidades de Pesca</label>
                        <ModalitySelector
                            selected={form.modalities}
                            onChange={(modalities) => setForm(prev => ({ ...prev, modalities }))}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className={labelClass}>¿Por qué recomendamos este material?</label>
                        <textarea value={form.recommendationReason} onChange={e => set('recommendationReason', e.target.value)} rows={2} className={cn(inputClass, 'resize-y')} placeholder="Explica por qué este producto es una buena recomendación..." />
                    </div>
                </div>

                {/* ── Section 3: Images ─────────────────────────────── */}
                <div className="space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <ImageIcon size={12} /> Imágenes ({allImages.length})
                    </p>

                    {allImages.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                            {allImages.map((url, i) => (
                                <div key={i} className="relative group w-24 h-24 rounded-lg overflow-hidden border border-border/50 bg-muted/20">
                                    <img src={url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" onError={e => { (e.target as HTMLImageElement).src = ''; }} />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(i)}
                                        className="absolute top-1 right-1 p-0.5 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X size={12} />
                                    </button>
                                    {i < form.images.length && (
                                        <span className="absolute bottom-1 left-1 text-[8px] bg-black/60 text-white px-1 rounded">URL</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div
                        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={cn(
                            'rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-all',
                            isDragOver
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border/50 hover:border-border text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Upload size={20} className="mx-auto mb-2 opacity-60" />
                        <p className="text-sm font-medium">Arrastra imágenes aquí o haz clic para seleccionar</p>
                        <p className="text-[10px] text-muted-foreground/60 mt-1">JPG, PNG, WebP · Máx. 10 MB por imagen</p>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={e => e.target.files && processFiles(e.target.files)}
                    />
                </div>

                {/* ── Actions ───────────────────────────────────────── */}
                <div className="flex items-center gap-3 pt-2 border-t border-border/30">
                    <button
                        type="submit"
                        disabled={isSaving || !form.title.trim() || !form.category || !form.waterType}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 shadow-sm"
                    >
                        {isSaving ? (
                            <><Loader2 size={14} className="animate-spin" /> Guardando...</>
                        ) : (
                            <><CheckCircle size={14} /> Crear y Aprobar</>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => { clearDraft(); onCancel(); }}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                        Cancelar
                    </button>
                </div>
            </form>
        </div>
    );
}
